// Apolo IDE — TaskOracleStatus (S2 Task Layer E4-WS1-T01).
//
// Shows per-task oracle verdict as a compact status badge.
// Data comes from a `verifyResult` prop (shape mirrors taskOracle.verifyTask output).
// No live oracle calls here — S4+ wires the actual verifiers.
//
// Statuses rendered:
//   reachedGreen=true, noVerifiers=false → ✓ oracle green  (rx-add)
//   reachedGreen=false, failed[0]        → ✗ <verifier>    (rx-del)
//   noVerifiers=true                     → nothing (honest: no verifier ran)
//   null / undefined                     → nothing

import { CheckCircle, XCircle } from 'lucide-react';

/**
 * @param {{
 *   verifyResult?: {
 *     reachedGreen: boolean
 *     noVerifiers: boolean
 *     failed: Array<{ verifier: string, evidence: string }>
 *   } | null
 *   compact?: boolean   — if true, icon-only (no text label)
 * }} props
 */
export function TaskOracleStatus({ verifyResult, compact = false }) {
  if (!verifyResult || verifyResult.noVerifiers) return null;

  const green = verifyResult.reachedGreen;
  const failedVerifier = !green && verifyResult.failed?.[0]?.verifier;

  return (
    <div
      data-testid="task-oracle-status"
      role="status"
      aria-label={green ? 'Oracle: verde' : `Oracle: falhou ${failedVerifier ?? ''}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: compact ? '2px 5px' : '3px 7px',
        borderRadius: 5,
        fontSize: compact ? 9 : 10,
        fontWeight: 600,
        background: green ? 'rgba(67,224,138,.1)' : 'rgba(255,122,122,.1)',
        color: green ? 'var(--rx-add)' : 'var(--rx-del)',
        borderLeft: `2px solid ${green ? 'var(--rx-add)' : 'var(--rx-del)'}`,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {green ? (
        <CheckCircle size={compact ? 9 : 10} aria-hidden="true" />
      ) : (
        <XCircle size={compact ? 9 : 10} aria-hidden="true" />
      )}
      {!compact && (
        <span>{green ? 'oracle ok' : failedVerifier ?? 'falhou'}</span>
      )}
    </div>
  );
}
